import { useCallback, memo, useEffect } from "react";
import {
  Button,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  AppBar,
  Toolbar,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import { Controller, useForm } from "react-hook-form";

type SoundMasterOptionDialogProps = {
  open: boolean;
  onClose: () => void;
};

type FormData = {
  nuanceSelect: "nuance" | "keyword";
  limit: number;
};

const SoundMasterOptionDialog = ({
  open,
  onClose,
}: SoundMasterOptionDialogProps) => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();
  const limit = useAppSelector((state) =>
    store.getAppState(state, "soundMasterLimit")
  );
  const nuanceSearch = useAppSelector((state) =>
    store.getAppState(state, "nuanceSearch")
  );
  const { handleSubmit, control, reset, watch, getValues } = useForm<FormData>({
    defaultValues: {
      nuanceSelect: "nuance",
      limit: 20,
    },
  });
  const nuanceSelect = watch("nuanceSelect");

  useEffect(() => {
    if (open) {
      reset({
        nuanceSelect: nuanceSearch ? "nuance" : "keyword",
        limit,
      });
    }
  }, [limit, nuanceSearch, reset, open]);

  const onSubmit = useCallback(
    (formData: FormData) => {
      dispatch(
        store.appStateMutate((state) => {
          state.soundMasterLimit = formData.limit;
          state.nuanceSearch =
            formData.nuanceSelect === "nuance" ? true : false;
        })
      );
      onClose();
    },
    [dispatch, onClose]
  );

  const handleDialogClose = useCallback(() => {
    onSubmit(getValues());
    onClose();
  }, [onSubmit, getValues, onClose]);

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="xs" fullWidth>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar variant="dense">
          <Typography variant="subtitle2">{t("検索設定")}</Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth sx={{ marginTop: "8px" }}>
            <InputLabel>{t("検索方法")}</InputLabel>
            <Controller
              name="nuanceSelect"
              control={control}
              render={({ field: { onChange, onBlur, value, name, ref } }) => (
                <Select
                  label={t("検索方法")}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  name={name}
                  inputRef={ref}
                >
                  <MenuItem value="nuance">{t("あいまい検索")}</MenuItem>
                  <MenuItem value="keyword">
                    {t("ワードを指定して検索")}
                  </MenuItem>
                </Select>
              )}
            />
            {nuanceSelect === "nuance" ? (
              <FormHelperText variant="outlined">
                {t(
                  "※あいまい検索は楽器、雰囲気、情景などで検索することができます。条件の似たBGMをより多く検索する際に最適です。"
                )}
              </FormHelperText>
            ) : (
              <FormHelperText variant="outlined">
                {t(
                  "※ワード検索は一致するキーワードで検索します。作曲者名や曲名での検索に最適です。"
                )}
              </FormHelperText>
            )}
            <FormHelperText variant="outlined">
              {t("※ワード検索とあいまい検索を併用することはできません。")}
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth sx={{ marginTop: "16px" }}>
            <InputLabel>{t("表示件数")}</InputLabel>
            <Controller
              name="limit"
              control={control}
              render={({ field: { onChange, onBlur, value, name, ref } }) => (
                <Select
                  label={t("表示件数")}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  name={name}
                  inputRef={ref}
                >
                  <MenuItem value={10}>10{t("件")}</MenuItem>
                  <MenuItem value={20}>20{t("件")}</MenuItem>
                  <MenuItem value={50}>50{t("件")}</MenuItem>
                </Select>
              )}
            />
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <Button fullWidth onClick={onClose} color="secondary">
          {t("キャンセル")}
        </Button>
        <Button fullWidth onClick={handleSubmit(onSubmit)} color="primary">
          {t("適用")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(SoundMasterOptionDialog);
